import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save, Stethoscope } from "lucide-react";
import {
  upsertMedicalHistorySchema,
  type UpsertMedicalHistoryInput,
} from "@shared/validation/medicalHistory.schema";
import { useMedicalHistory, useSaveMedicalHistory } from "../../lib/api/useMedicalHistory";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { SectionCard } from "../ui/SectionCard";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { SECTION_ACCENT } from "../../lib/accents";
import { INLINE_STATUS } from "../../lib/surfaces";

interface FormShape {
  primaryPhysicianName: string;
  primaryPhysicianPhone: string;
  dentistName: string;
  dentistPhone: string;
  bloodType: string;
  conditions: string;
  pastIllnesses: string;
  hospitalizations: string;
  insuranceNotes: string;
  notes: string;
}

const EMPTY: FormShape = {
  primaryPhysicianName: "",
  primaryPhysicianPhone: "",
  dentistName: "",
  dentistPhone: "",
  bloodType: "",
  conditions: "",
  pastIllnesses: "",
  hospitalizations: "",
  insuranceNotes: "",
  notes: "",
};

function nullToEmpty(value: string | null): string {
  return value ?? "";
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function MedicalHistorySection({ childId }: { childId: string }) {
  const { data: history, isLoading, error } = useMedicalHistory(childId);
  const saveMutation = useSaveMedicalHistory(childId);
  const { publish } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormShape>({
    resolver: zodResolver(upsertMedicalHistorySchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (history) {
      reset({
        primaryPhysicianName: nullToEmpty(history.primaryPhysicianName),
        primaryPhysicianPhone: nullToEmpty(history.primaryPhysicianPhone),
        dentistName: nullToEmpty(history.dentistName),
        dentistPhone: nullToEmpty(history.dentistPhone),
        bloodType: nullToEmpty(history.bloodType),
        conditions: nullToEmpty(history.conditions),
        pastIllnesses: nullToEmpty(history.pastIllnesses),
        hospitalizations: nullToEmpty(history.hospitalizations),
        insuranceNotes: nullToEmpty(history.insuranceNotes),
        notes: nullToEmpty(history.notes),
      });
    }
  }, [history, reset]);

  const onSubmit = async (data: FormShape) => {
    const payload: UpsertMedicalHistoryInput = {
      primaryPhysicianName: emptyToNull(data.primaryPhysicianName),
      primaryPhysicianPhone: emptyToNull(data.primaryPhysicianPhone),
      dentistName: emptyToNull(data.dentistName),
      dentistPhone: emptyToNull(data.dentistPhone),
      bloodType: emptyToNull(data.bloodType),
      conditions: emptyToNull(data.conditions),
      pastIllnesses: emptyToNull(data.pastIllnesses),
      hospitalizations: emptyToNull(data.hospitalizations),
      insuranceNotes: emptyToNull(data.insuranceNotes),
      notes: emptyToNull(data.notes),
    };
    try {
      await saveMutation.mutateAsync(payload);
      publish({ variant: "success", title: "Medical history saved successfully" });
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to save medical history",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <SectionCard icon={Stethoscope} title="Medical History" accent={SECTION_ACCENT.medical}>
      {isLoading ? (
        <div role="status" className={INLINE_STATUS}>
          <Spinner size="sm" />
          Loading medical history…
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Failed to load medical history: {error.message}</p>
      ) : (
        <>
          <p className="text-sm text-neutral-600">
            Physician, conditions, and history a caregiver may need in an emergency.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="mh-phys-name" className="text-sm font-medium text-neutral-800">
                  Primary Physician
                </label>
                <input
                  id="mh-phys-name"
                  type="text"
                  {...register("primaryPhysicianName")}
                  className={INPUT_CLASS}
                  placeholder="Dr. name"
                />
              </div>
              <div>
                <label htmlFor="mh-phys-phone" className="text-sm font-medium text-neutral-800">
                  Physician Phone
                </label>
                <input
                  id="mh-phys-phone"
                  type="tel"
                  {...register("primaryPhysicianPhone")}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="mh-dentist-name" className="text-sm font-medium text-neutral-800">
                  Dentist
                </label>
                <input
                  id="mh-dentist-name"
                  type="text"
                  {...register("dentistName")}
                  className={INPUT_CLASS}
                  placeholder="Dr. name"
                />
              </div>
              <div>
                <label htmlFor="mh-dentist-phone" className="text-sm font-medium text-neutral-800">
                  Dentist Phone
                </label>
                <input
                  id="mh-dentist-phone"
                  type="tel"
                  {...register("dentistPhone")}
                  className={INPUT_CLASS}
                />
              </div>
            </div>

            <div className="sm:max-w-[12rem]">
              <label htmlFor="mh-blood" className="text-sm font-medium text-neutral-800">
                Blood Type
              </label>
              <input
                id="mh-blood"
                type="text"
                {...register("bloodType")}
                className={INPUT_CLASS}
                placeholder="e.g., O+"
              />
              {errors.bloodType?.message && (
                <p className="mt-1 text-sm text-red-600">{errors.bloodType.message}</p>
              )}
            </div>

            <MhTextarea id="mh-conditions" label="Conditions" register={register("conditions")} placeholder="Ongoing conditions, diagnoses…" />
            <MhTextarea id="mh-past" label="Past Illnesses" register={register("pastIllnesses")} placeholder="Significant past illnesses" />
            <MhTextarea id="mh-hosp" label="Hospitalizations / Surgeries" register={register("hospitalizations")} placeholder="Dates and reasons" />
            <MhTextarea id="mh-insurance" label="Insurance Notes" register={register("insuranceNotes")} placeholder="Provider, policy/member reference" />
            <MhTextarea id="mh-notes" label="Additional Notes" register={register("notes")} placeholder="Anything else a caregiver should know" />

            <div className="flex items-center justify-end">
              <Button type="submit" disabled={!isDirty} isLoading={isSubmitting || saveMutation.isPending}>
                {saveMutation.isPending ? (
                  "Saving…"
                ) : (
                  <>
                    <Save className="h-4 w-4" aria-hidden />
                    Save Medical History
                  </>
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </SectionCard>
  );
}

function MhTextarea({
  id,
  label,
  placeholder,
  register,
}: {
  id: string;
  label: string;
  placeholder: string;
  register: ReturnType<ReturnType<typeof useForm<FormShape>>["register"]>;
}) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-neutral-800">
        {label}
      </label>
      <textarea id={id} rows={2} placeholder={placeholder} {...register} className={INPUT_CLASS} />
    </div>
  );
}
